import {ReactNode} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {useAuth} from '../hooks/useAuth';
import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {SelecteurDeLangue} from './SelecteurDeLangue';
import {Bug, CaretDown, DeviceMobile, Headset, House, Plus, SignOut, UsersThree} from '@phosphor-icons/react';
import {ADMIN_ROOT, appVersionsPath, bugSynthesisPath, bugsPath, PUBLIC_FORM_URL, teamMembersPath} from '../routes';

interface LayoutProps {
  children: ReactNode;
}


export function Layout({ children }: LayoutProps) {
  const t = useTraduire();
  const { user, signOut } = useAuth();
  const location = useLocation();

  const navLinkClass = (path: string) => {
    const isActive = location.pathname === path;
    return `px-3 py-2 rounded-pill text-sm font-medium transition-colors ${
        isActive
            ? 'bg-primary/10 text-primary'
            : 'text-gray-500 hover:text-primary hover:bg-primary/5'
    }`;
  };


  /** Actif des que l'une des routes du groupe est ouverte. */
  const navGroupClass = (paths: string[]) => {
    const isActive = paths.includes(location.pathname);
    return `px-3 py-2 rounded-pill text-sm font-medium transition-colors ${
        isActive
            ? 'bg-primary/10 text-primary'
            : 'text-gray-500 hover:text-primary hover:bg-primary/5'
    }`;
  };

  const submenuItemClass = (path: string) => {
    const isActive = location.pathname === path;
    return `block px-4 py-2 text-sm whitespace-nowrap transition-colors ${
        isActive ? 'text-primary bg-primary/5' : 'text-gray-600 hover:text-primary hover:bg-primary/5'
    }`;
  };

  return (
      <div className="min-h-screen bg-[#f0f0f0]">
        {/* Header — white with logo tinted in primary color */}
        <header className="bg-white shadow-bo border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[52px]">
            <div className="flex items-center gap-6 sm:gap-8">
              <Link to={ADMIN_ROOT} className="flex items-center gap-3">
                <img src="/assimil-logo.svg" alt="Assimil" className="h-8"/>
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#f5012e]">
                  <Headset size={16} weight="bold" color="#ffffff"/>
                </span>
              </Link>
              <nav className="flex gap-1 sm:gap-2">
                <Link to={ADMIN_ROOT} className={`${navLinkClass(ADMIN_ROOT)} flex items-center gap-1.5`}>
                  <House size={18} weight="regular"/>
                </Link>
                {/* Ouvrir une demande soi-meme, par le formulaire du site —
                    celui-la meme que remplissent les gens qui nous ecrivent. */}
                <a
                    href={PUBLIC_FORM_URL}
                    target="_blank"
                    rel="noopener"
                    className={`${navLinkClass('')} flex items-center gap-1.5`}
                >
                  <Plus size={18} weight="regular"/>
                  <span className="hidden sm:inline">{t(T.creer)}</span>
                </a>
                <Link to={teamMembersPath} className={`${navLinkClass(teamMembersPath)} flex items-center gap-1.5`}>
                  <UsersThree size={18} weight="regular"/>
                  <span className="hidden sm:inline">{t(T.membres)}</span>
                </Link>
                <Link to={appVersionsPath} className={`${navLinkClass(appVersionsPath)} flex items-center gap-1.5`}>
                  <DeviceMobile size={18} weight="regular"/>
                  <span className="hidden sm:inline">{t(T.versions)}</span>
                </Link>
                {/* Sous-menu bugs. Ouvert au survol et au focus clavier :
                    group-focus-within evite que l'entree soit inatteignable
                    sans souris. */}
                <div className="relative group">
                  <button
                      type="button"
                      className={`${navGroupClass([bugsPath, bugSynthesisPath])} flex items-center gap-1.5`}
                  >
                    <Bug size={18} weight="regular"/>
                    <span className="hidden sm:inline">{t(T.bugs)}</span>
                    <CaretDown size={12} weight="regular"/>
                  </button>
                  <div className="absolute left-0 top-full pt-1 z-20 hidden group-hover:block group-focus-within:block">
                    <div className="bg-white rounded-lg shadow-bo border border-gray-200 py-1 min-w-[200px]">
                      <Link to={bugSynthesisPath} className={submenuItemClass(bugSynthesisPath)}>
                        Détecter
                      </Link>
                      <Link to={bugsPath} className={submenuItemClass(bugsPath)}>
                        Liste des bugs
                      </Link>
                    </div>
                  </div>
                </div>
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <SelecteurDeLangue/>
              <span className="text-xs text-gray-400 hidden md:inline">{user?.email}</span>
              <button
                onClick={signOut}
                className="px-3 py-1.5 rounded-pill text-xs font-medium text-gray-500 hover:text-primary hover:bg-primary/5 transition-colors flex items-center gap-2"
              >
                <SignOut size={18} weight="regular" className="inline-block"/>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>
    </div>
  );
}
